// src/utils/driveService.js
// Puente del cliente hacia las Edge Functions de Google Drive.
//
// El browser nunca habla con Google ni con la tabla `drive_connections`.
// Solo pidele a las funciones y ellas, que tienen el service role, hacen de
// puente. Asi el refresh token no existe en ningun momento en el cliente.
import { supabase } from "../Supabase/SupabaseClient";

/**
 * Las Edge Functions contestan { error: "mensaje" } con status 4xx/5xx.
 * supabase-js lo envuelve en un FunctionsHttpError que tira el cuerpo a
 * `error.context`, asi que sin leerlo el front veria "Edge Function returned
 * a non-2xx status code" en vez del motivo real.
 */
const mensajeReal = async (error) => {
  const ctx = error?.context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const cuerpo = await ctx.json();
      if (cuerpo?.error) return cuerpo.error;
    } catch {
      /* la respuesta no era JSON: se cae al mensaje generico */
    }
  }
  return error?.message || "No se pudo completar la operacion.";
};

const invoke = async (nombre, body) => {
  const { data, error } = await supabase.functions.invoke(nombre, {
    body: body ?? {},
  });

  if (error) throw new Error(await mensajeReal(error));
  if (data?.error) throw new Error(data.error);
  return data ?? {};
};

/**
 * Estado de la conexion de Drive del usuario.
 * @returns {Promise<{conectado: boolean, revocado?: boolean, correo?: string|null,
 *   carpetaId?: string|null, carpetaNombre?: string|null}>}
 */
export const estadoDrive = () => invoke("drive-status");

/**
 * Devuelve la URL de autorizacion de Google. El que la abre es el front, con
 * un `window.location.href`: el popup lo bloquea el navegador y no hay forma
 * de saber cuando el usuario termino en la pantalla de permisos.
 */
export const conectarDrive = async () => {
  const { url } = await invoke("drive-auth-start");
  if (!url) throw new Error("Supabase no devolvio la URL de autorizacion.");
  return url;
};

/** Borra la conexion local e intenta revocar el permiso en Google. */
export const desconectarDrive = () => invoke("drive-disconnect");

/** Lista carpetas del Drive del usuario. `parentId` null = raiz. */
export const listarCarpetas = (parentId = null, pageToken = undefined) =>
  invoke("drive-folders", { parentId, pageToken });

/**
 * Sube una tanda de fotos y devuelve el estado de la sesion.
 *
 * La funcion procesa unas pocas por llamada a proposito: una Edge Function
 * tiene limite de tiempo y una sesion puede tener cientos de fotos. El front
 * llama en bucle hasta que `terminado` da true.
 */
export const subirTanda = ({ sesionId, folderId, folderName, lote }) =>
  invoke("drive-upload", { sesionId, folderId, folderName, lote });

/** Link a la carpeta de Drive donde quedaron las fotos. */
export const linkCarpetaDrive = (folderId) =>
  folderId ? `https://drive.google.com/drive/folders/${folderId}` : null;